import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronDown, ChevronRight, Download, ExternalLink, FileText, Menu, Search, X } from 'lucide-react'
import { publicFaqExtendedItems } from '../../marketing/publicFaqExtended'
import {
  groupManualItems,
  supportGuideIntroArticles,
  supportGuideManualFallbackItems,
  withManualCategory,
} from '../../content/supportGuideManuals'
import { PUBLIC_INQUIRY_PATH } from '../../marketing/marketingNav'
import { api, absoluteApiV1Path } from '../../lib/api'
import { SupportGuideManualContent } from '../../components/SupportGuideManualModal'

function resolveManualLink(href) {
  if (!href) return null
  const value = String(href).trim()
  if (!value) return null
  if (/^https?:\/\//i.test(value)) return value
  const path = value.startsWith('/') ? value.slice(1) : value
  return path.startsWith('public/') ? absoluteApiV1Path(path) : value
}

function manualAssetUrl(path) {
  return absoluteApiV1Path(`public/support-guide/manual-assets/${String(path || '').replace(/^\/+/, '')}`)
}

function isAppManualItem(item) {
  const id = String(item?.id || item?.slug || '')
  return item?.category === 'app' || id.startsWith('app-')
}

function isRetiredManualItem(item) {
  const id = String(item?.id || item?.slug || '')
  return id === 'bulk-sms'
}

function unwrapData(res) {
  const payload = res?.data
  return payload?.data !== undefined ? payload.data : (payload ?? null)
}

function matchesGuideItem(item, query) {
  const keyword = String(query || '').trim().toLocaleLowerCase('ko-KR')
  if (!keyword) return true
  return [item?.title, item?.summary, item?.categoryLabel]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('ko-KR')
    .includes(keyword)
}

function SidebarManualGroup({
  label,
  items,
  activeId,
  expanded,
  onToggle,
  onSelect,
  collapsible = true,
}) {
  if (!items.length) return null

  return (
    <section className="mt-4">
      {collapsible ? (
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm font-bold text-slate-800 hover:bg-slate-50"
          aria-expanded={expanded}
          onClick={onToggle}
        >
          <span>{label}</span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? '' : '-rotate-90'}`} aria-hidden="true" />
        </button>
      ) : (
        <p className="px-3 py-2 text-sm font-bold text-slate-800">{label}</p>
      )}
      {expanded ? (
        <ol className="mt-1 space-y-0.5">
          {items.map((item, index) => {
            const active = item.id === activeId
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                    active ? 'bg-sky-50 font-bold text-brand-navy' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                  aria-current={active ? 'page' : undefined}
                  title={item.title}
                  onClick={() => onSelect(item)}
                >
                  <span className="w-5 shrink-0 text-xs text-slate-400">{String(index + 1).padStart(2, '0')}</span>
                  <span className="min-w-0 flex-1 truncate">{item.title}</span>
                </button>
              </li>
            )
          })}
        </ol>
      ) : null}
    </section>
  )
}

function GuideTableOfContents({ items, activeId, onSelect }) {
  if (!items.length) return null

  return (
    <aside className="hidden w-56 shrink-0 xl:block" aria-label="이 문서의 목차">
      <div className="sticky top-20 border-l border-slate-200 py-1 pl-5">
        <p className="mb-3 text-xs font-bold text-slate-500">이 문서의 목차</p>
        <ol className="space-y-1">
          {items.map((item) => {
            const active = item.id === activeId
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={`block w-full border-l-2 py-1.5 pl-3 pr-2 text-left text-sm leading-5 transition-colors ${
                    active
                      ? 'border-brand-navy font-bold text-brand-navy'
                      : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-900'
                  }`}
                  onClick={() => onSelect(item.id)}
                >
                  {item.title}
                </button>
              </li>
            )
          })}
        </ol>
      </div>
    </aside>
  )
}

function GuideArticlePanel({ article, manualById, onSelectManual }) {
  const tocItems = article.sections.map((section) => ({ id: section.id, title: section.title, kind: 'section' }))
  const [activeTocId, setActiveTocId] = useState(tocItems[0]?.id || '')

  useEffect(() => {
    setActiveTocId(article.sections[0]?.id || '')
  }, [article.id, article.sections])

  const scrollToSection = (id) => {
    setActiveTocId(id)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <section className="min-w-0">
      <header className="border-b border-slate-200 px-5 py-8 sm:px-8 sm:py-10">
        <div className="mx-auto w-full max-w-[1120px]">
          <p className="text-sm font-bold text-sky-700">{article.categoryLabel}</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">{article.title}</h1>
          <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">{article.summary}</p>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-[1120px] gap-10 px-5 py-10 sm:px-8">
        <article className="min-w-0 flex-1 space-y-14">
          {article.sections.map((section, sectionIndex) => (
            <section id={section.id} key={section.id} className="scroll-mt-20 border-b border-slate-200 pb-14 last:border-b-0 last:pb-0">
              <h2 className="text-2xl font-black tracking-tight text-slate-950">{sectionIndex + 1}. {section.title}</h2>
              {section.image ? (
                <figure className="mt-6 overflow-hidden border border-slate-200 bg-white">
                  <img src={manualAssetUrl(section.image)} alt={section.imageAlt || section.title} className="block h-auto w-full" />
                </figure>
              ) : null}
              <ol className="mt-6 space-y-5">
                {section.steps.map((step, stepIndex) => {
                  const manual = step.manualId ? manualById.get(step.manualId) : null
                  return (
                    <li key={`${section.id}-${step.title}`} className="flex gap-4">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-xs font-black text-brand-navy">
                        {stepIndex + 1}
                      </span>
                      <div className="min-w-0 pt-0.5">
                        {manual ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-left text-base font-bold text-slate-900 hover:text-brand-navy"
                            onClick={() => onSelectManual(manual)}
                          >
                            <span>{step.title}</span>
                            <ChevronRight className="h-4 w-4" aria-hidden="true" />
                          </button>
                        ) : (
                          <h3 className="text-base font-bold text-slate-900">{step.title}</h3>
                        )}
                        <p className="mt-1 text-sm leading-6 text-slate-600">{step.description}</p>
                      </div>
                    </li>
                  )
                })}
              </ol>
            </section>
          ))}
        </article>
        <GuideTableOfContents items={tocItems} activeId={activeTocId} onSelect={scrollToSection} />
      </div>
    </section>
  )
}

function FaqPanel() {
  return (
    <section className="mx-auto w-full max-w-[860px] px-5 py-8 sm:px-8 sm:py-10">
      <p className="text-sm font-bold text-sky-700">도움말</p>
      <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950">자주 묻는 질문</h1>
      <div className="mt-8 divide-y border-y border-slate-200">
        {publicFaqExtendedItems.map((item) => (
          <details key={item.question} className="group py-1">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-base font-bold text-slate-900 marker:hidden">
              <span>{item.question}</span>
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-400 transition-transform group-open:rotate-180" aria-hidden="true" />
            </summary>
            <p className="pb-5 pr-8 text-sm leading-7 text-slate-600">{item.answer}</p>
          </details>
        ))}
      </div>
      <Link
        to={PUBLIC_INQUIRY_PATH}
        className="mt-8 inline-flex items-center rounded-md bg-brand-navy px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-navy/90"
      >
        문의하기
      </Link>
    </section>
  )
}

function ManualPanel({ manual }) {
  const [tocItems, setTocItems] = useState([])
  const [selectedAnchor, setSelectedAnchor] = useState('')
  const isApp = isAppManualItem(manual)

  useEffect(() => {
    setTocItems([])
    setSelectedAnchor('')
  }, [manual.id])

  const selectToc = (id) => {
    setSelectedAnchor(id)
  }

  return (
    <section className="min-w-0">
      <header className="border-b border-slate-200 px-5 py-8 sm:px-8 sm:py-10">
        <div className="mx-auto flex w-full max-w-[1120px] items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-bold text-sky-700">{isApp ? '앱 가이드' : (manual.categoryLabel || '기능 가이드')}</p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">{manual.title}</h1>
            {manual.summary ? <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">{manual.summary}</p> : null}
          </div>
          {manual.downloadUrl ? (
            <a
              href={manual.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:border-sky-300 hover:bg-sky-50 hover:text-brand-navy"
              aria-label={`${manual.title} PDF 다운로드`}
              title="PDF 다운로드"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
            </a>
          ) : null}
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-[1120px] gap-10 px-5 sm:px-8">
        <div className="min-w-0 flex-1">
          <SupportGuideManualContent
            key={manual.id}
            slug={manual.id}
            title={manual.title}
            anchor={selectedAnchor}
            continuous
            fitContent
            onTableOfContentsChange={setTocItems}
            className="w-full"
            iframeClassName="block w-full border-0 bg-white"
          />
        </div>
        <GuideTableOfContents items={tocItems} activeId={selectedAnchor} onSelect={selectToc} />
      </div>
    </section>
  )
}

export default function SupportGuidePage() {
  const [manualItems, setManualItems] = useState(supportGuideManualFallbackItems)
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState({
    sales: true,
    customer: true,
    design: true,
    work: false,
    admin: false,
    app: true,
  })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const response = await api.get('/public/support-guide/manuals')
        const data = unwrapData(response)
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setManualItems(data.map((row) => withManualCategory({
            id: row.slug || String(row.id),
            title: row.title,
            summary: row.summary,
            category: row.category,
            categoryLabel: row.categoryLabel,
            viewUrl: resolveManualLink(row.viewUrl),
            downloadUrl: resolveManualLink(row.downloadUrl),
          })))
        }
      } catch {
        if (!cancelled) setManualItems(supportGuideManualFallbackItems)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const guideManualItems = useMemo(() => manualItems.filter((item) => !isAppManualItem(item) && !isRetiredManualItem(item)), [manualItems])
  const groupedWebManuals = useMemo(() => groupManualItems(guideManualItems), [guideManualItems])
  const manualById = useMemo(() => {
    const next = new Map()
    supportGuideManualFallbackItems.filter((item) => !isAppManualItem(item) && !isRetiredManualItem(item)).forEach((item) => next.set(item.id, item))
    manualItems.filter((item) => !isAppManualItem(item) && !isRetiredManualItem(item)).forEach((item) => next.set(item.id, item))
    return next
  }, [manualItems])

  const selectedManualId = searchParams.get('manual') || ''
  const selectedManual = manualById.get(selectedManualId) || null
  const selectedArticleId = searchParams.get('article') || supportGuideIntroArticles[0].id
  const selectedArticle = supportGuideIntroArticles.find((item) => item.id === selectedArticleId) || supportGuideIntroArticles[0]
  const requestedView = searchParams.get('view') || 'article'
  const activeView = requestedView === 'manual' && selectedManual
    ? 'manual'
    : requestedView === 'faq'
      ? 'faq'
      : 'article'
  const hasSearch = Boolean(search.trim())
  const visibleIntroArticles = useMemo(
    () => supportGuideIntroArticles.filter((item) => matchesGuideItem(item, search)),
    [search],
  )
  const visibleWebGroups = useMemo(() => groupedWebManuals
    .map((group) => ({ ...group, items: group.items.filter((item) => matchesGuideItem(item, search)) }))
    .filter((group) => group.items.length > 0), [groupedWebManuals, search])

  const selectArticle = (article) => {
    setSearchParams({ view: 'article', article: article.id })
    setMobileNavOpen(false)
  }

  const selectManual = (manual) => {
    setSearchParams({ view: 'manual', manual: manual.id })
    setExpandedGroups((current) => ({ ...current, [manual.category]: true }))
    setMobileNavOpen(false)
  }

  const selectFaq = () => {
    setSearchParams({ view: 'faq' })
    setMobileNavOpen(false)
  }

  const toggleGroup = (groupId) => {
    setExpandedGroups((current) => ({ ...current, [groupId]: !current[groupId] }))
  }

  const sidebarGroups = hasSearch ? visibleWebGroups : groupedWebManuals
  const sidebarIntroArticles = hasSearch ? visibleIntroArticles : supportGuideIntroArticles
  const searchResultCount = visibleIntroArticles.length
    + visibleWebGroups.reduce((total, group) => total + group.items.length, 0)

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="flex h-14 items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-navy text-xs font-black text-white">EZ</span>
            <span className="text-sm font-black text-slate-950">설계EZ</span>
            <span className="h-4 w-px bg-slate-200" aria-hidden="true" />
            <span className="truncate text-sm font-semibold text-slate-600">이용가이드</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 hover:border-sky-300 hover:bg-sky-50"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              서비스 바로가기
            </Link>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 hover:border-sky-300 hover:bg-sky-50 lg:hidden"
              aria-expanded={mobileNavOpen}
              aria-controls="support-guide-navigation"
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              {mobileNavOpen ? <X className="h-4 w-4" aria-hidden="true" /> : <Menu className="h-4 w-4" aria-hidden="true" />}
              가이드 목록
            </button>
          </div>
        </div>
      </header>

      <div className="grid min-h-[calc(100dvh-3.5rem)] lg:grid-cols-[290px_minmax(0,1fr)]">
        <aside
          id="support-guide-navigation"
          className={`${mobileNavOpen ? 'block' : 'hidden'} min-w-0 border-b border-slate-200 bg-slate-50/70 lg:sticky lg:top-14 lg:block lg:h-[calc(100dvh-3.5rem)] lg:overflow-y-auto lg:border-b-0 lg:border-r`}
        >
          <div className="border-b border-slate-200 p-4">
            <label className="flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-slate-500 focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-100">
              <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                placeholder="가이드 검색"
                aria-label="가이드 검색"
              />
            </label>
          </div>

          <nav className="p-3" aria-label="이용가이드 목록">
            {hasSearch ? (
              <>
                <p className="px-3 pt-2 text-xs font-bold text-slate-500">검색 결과</p>
                <SidebarManualGroup
                  label="설계EZ 안내"
                  items={sidebarIntroArticles}
                  activeId={activeView === 'article' ? selectedArticle.id : ''}
                  expanded
                  collapsible={false}
                  onSelect={selectArticle}
                />
                {sidebarGroups.map((group) => (
                  <SidebarManualGroup
                    key={group.id}
                    label={group.label}
                    items={group.items}
                    activeId={activeView === 'manual' ? selectedManualId : ''}
                    expanded
                    collapsible={false}
                    onSelect={selectManual}
                  />
                ))}
                {searchResultCount === 0 ? <p className="px-3 py-5 text-sm text-slate-500">일치하는 가이드가 없습니다.</p> : null}
              </>
            ) : (
              <>
                <SidebarManualGroup
                  label="설계EZ 안내"
                  items={sidebarIntroArticles}
                  activeId={activeView === 'article' ? selectedArticle.id : ''}
                  expanded
                  collapsible={false}
                  onSelect={selectArticle}
                />

                <div className="mt-5 border-t border-slate-200 pt-3">
                  <p className="px-3 text-xs font-bold text-slate-500">기능별 가이드</p>
                  {sidebarGroups.map((group) => (
                    <SidebarManualGroup
                      key={group.id}
                      label={group.label}
                      items={group.items}
                      activeId={activeView === 'manual' ? selectedManualId : ''}
                      expanded={Boolean(expandedGroups[group.id])}
                      onToggle={() => toggleGroup(group.id)}
                      onSelect={selectManual}
                    />
                  ))}
                </div>


                <div className="mt-5 border-t border-slate-200 pt-3">
                  <button
                    type="button"
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                      activeView === 'faq' ? 'bg-sky-50 font-bold text-brand-navy' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                    aria-current={activeView === 'faq' ? 'page' : undefined}
                    onClick={selectFaq}
                  >
                    <FileText className="h-4 w-4" aria-hidden="true" />
                    자주 묻는 질문
                  </button>
                </div>
              </>
            )}
          </nav>
        </aside>

        <main className="min-w-0 bg-white">
          {activeView === 'manual' && selectedManual ? (
            <ManualPanel manual={selectedManual} />
          ) : activeView === 'faq' ? (
            <FaqPanel />
          ) : (
            <GuideArticlePanel article={selectedArticle} manualById={manualById} onSelectManual={selectManual} />
          )}
        </main>
      </div>
    </div>
  )
}